(ns ui.editor2.giphy
  "The Giphy search of the editor2 GIF sidebar (`ui.editor2.sidebar-giphy`):
   the request to the Giphy API and the action handlers of the panel.

   The panel state lives in the store under `panel-path`, tagged with the
   `:panel-key` of the sidebar (`[:editor2 :giphy-sidebar :panel-key]`). The
   sidebar mounts the panel per panel-key, and each mount resets the state
   and loads the first page again. A response only lands if its panel is
   still the current one and it answers the latest request."
  (:require [goog.functions :as functions]))

(def apikey
  "gRW8HSsxMzbOdb42FjyvI35Ux31qswPU")

(def endpoint-url
  "https://api.giphy.com/v1/")

(def number-of-gifs
  20)

(def panel-path
  [:editor2 :giphy-panel])

(def default-panel-state
  {:search "trending?"
   :search-value ""
   :search-offset 0
   :mode :stickers})

(defn search-fragment
  [search-value]
  (if (= "" search-value)
    "trending?"
    (str "search?q=" (js/encodeURIComponent search-value) "&")))

(defn api-url
  [{:keys [mode search search-offset]}]
  (str endpoint-url
       (name mode)
       "/"
       search
       "api_key=" apikey
       "&limit=" number-of-gifs
       "&offset=" (or search-offset 0)))

(defn current-giphy-state
  "The part of the panel state that is remembered per Giphy asset, so the
   sidebar reopens a GIF clip with the search that found it (not with a
   keyword that was typed but not searched)."
  [panel]
  (-> (select-keys panel
                   [:mode
                    :search
                    :search-offset])
      (assoc :search-value (:query panel ""))))

(defn gif-asset
  "The editor2 asset for a GIF of the Giphy API: a sticker is added as
   animated GIF, a GIF as its MP4 video."
  [gif]
  (let [sticker? (= 1 (:is_sticker gif))
        video (if sticker?
                (get-in gif [:images :original])
                (get-in gif [:images :original_mp4]))]
    {:db/id "gif-asset"
     :asset/uuid (random-uuid)
     :asset/type (if sticker?
                   :gif
                   :video)
     :asset/uri (if sticker?
                  (:url video)
                  (:mp4 video))
     :asset/name (str "giphy-" (:slug gif))
     :asset/width (js/parseInt (:width video))
     :asset/height (js/parseInt (:height video))}))

(defn update-panel
  "Updates the panel state, but only while the panel of `panel-key` is the
   current one (it is gone after the panel unmounted)."
  [state panel-key f & args]
  (if (and (some? panel-key)
           (= panel-key
              (get-in state (conj panel-path :panel-key))))
    (apply update-in state panel-path f args)
    state))

(defn- update-panel!
  [store panel-key f & args]
  (swap! store #(apply update-panel % panel-key f args)))

(defn receive-gifs
  "Shows the `gifs` of the request `request-id`: a new search replaces the
   results, \"Load more\" appends them. Unless a newer request was started
   (or is about to start, see `start-search`)."
  [panel request-id gifs]
  (if (= request-id (:request-id panel))
    (-> panel
        (assoc :gifs (if (:loading-more? panel)
                       (into (vec (:gifs panel)) gifs)
                       (vec gifs))
               :more? (= (count gifs) number-of-gifs)
               :searching? false
               :loading-more? false)
        (dissoc :error?))
    panel))

(defn receive-error
  "Offers to try again. A failed \"Load more\" keeps the results that are
   already shown; a failed new search has no results, the previous ones
   belong to another search."
  [panel request-id]
  (if (= request-id (:request-id panel))
    (-> panel
        (assoc :error? true
               :searching? false
               :loading-more? false)
        (cond->
          ;; The failed page was not loaded:
          (:loading-more? panel)
          (update :search-offset - number-of-gifs)

          (not (:loading-more? panel))
          (assoc :gifs [])))
    panel))

(defn fetch-gifs!
  "Loads the page at `:search-offset` of the current panel state from Giphy.
   Does nothing without a panel: a debounced call can fire after the panel
   was closed."
  [store]
  (when-let [panel (get-in @store panel-path)]
    (let [panel-key (:panel-key panel)
          request-id (random-uuid)]
      (update-panel! store panel-key assoc :request-id request-id)
      (-> (js/fetch (api-url panel))
          (.then (fn [response]
                   (if (.-ok response)
                     (.json response)
                     (throw (js/Error. (str "Giphy responded " (.-status response)))))))
          (.then (fn [body]
                   (update-panel! store panel-key receive-gifs request-id
                                  (:data (js->clj body :keywordize-keys true)))))
          (.catch (fn [error]
                    (js/console.warn "Giphy request failed:" error)
                    (update-panel! store panel-key receive-error request-id)))))))

(def fetch-gifs-debounced!
  ;; Switching tabs quickly sends only the last request:
  (functions/debounce fetch-gifs! 250))

(defn start-search
  "Applies the change of the search, searches the keyword of the input
   from the first page on and invalidates the running request: its
   response would otherwise land during the debounce of the new one (e.g.
   trending stickers under the just selected GIFs tab).

   `:search` (the URL fragment) and `:query` (the keyword of the caption)
   only change here, so typing without a search doesn't change what
   \"Load more\" fetches."
  [panel f & args]
  (let [panel (apply f panel args)
        query (:search-value panel "")]
    (-> panel
        (assoc :search (search-fragment query)
               :query query
               :search-offset 0
               :searching? true
               :loading-more? false)
        (dissoc :request-id :error?))))

(defn init!
  "Starts the panel with the remembered search of the GIF clip (or the
   trending stickers). It always starts at the first page; the remembered
   `:search-offset` only tells how far the user had loaded."
  [{:keys [store action]}]
  (let [[_ panel-key initial-state] action]
    (swap! store assoc-in panel-path
           (start-search (merge default-panel-state
                                (select-keys initial-state [:mode :search-value])
                                {:panel-key panel-key})
                         identity))
    (fetch-gifs! store)))

(defn- search-with!
  [store f & args]
  (swap! store update-in panel-path
         (fn [panel]
           (apply start-search panel f args)))
  (fetch-gifs-debounced! store))

(defn start-load-more
  "The next page, appended to the results."
  [panel]
  (-> panel
      (update :search-offset (fnil + 0) number-of-gifs)
      (assoc :loading-more? true)
      (dissoc :request-id :error?)))

(defn load-more!
  [{:keys [store]}]
  (let [panel (get-in @store panel-path)]
    (when-not (or (:searching? panel)
                  (:loading-more? panel))
      (swap! store update-in panel-path start-load-more)
      (fetch-gifs! store))))

(defn unmount
  "Drops the panel state, unless a newer panel already took it over."
  [state panel-key]
  (if (= panel-key
         (get-in state (conj panel-path :panel-key)))
    (update state :editor2 dissoc (last panel-path))
    state))

(defn select!
  "Adds the GIF as clip, or replaces the GIF of the clip the sidebar was
   opened for."
  [{:keys [store action ui/event-handler]}]
  (let [[_ gif] action
        giphy-state (assoc (current-giphy-state (get-in @store panel-path))
                           :id (:id gif))]
    (event-handler {}
                   [[:editor2/add-or-replace-giphy-asset (gif-asset gif) giphy-state]
                    [:editor2/prepare-assets!]])))

(def giphy-register
  [{:ui.action/kind :editor2.giphy/init
    :ui.action/handler init!}
   {:ui.action/kind :editor2.giphy/set-search-value
    :ui.action/handler (fn [{:keys [store action]}]
                         (swap! store assoc-in
                                (conj panel-path :search-value)
                                (second action)))}
   {:ui.action/kind :editor2.giphy/search
    :ui.action/handler (fn [{:keys [store]}]
                         (search-with! store identity))}
   {:ui.action/kind :editor2.giphy/clear-search
    :ui.action/handler (fn [{:keys [store]}]
                         (search-with! store assoc :search-value ""))}
   {:ui.action/kind :editor2.giphy/set-mode
    :ui.action/handler (fn [{:keys [store action]}]
                         (search-with! store assoc :mode (second action)))}
   {:ui.action/kind :editor2.giphy/load-more
    :ui.action/handler load-more!}
   {:ui.action/kind :editor2.giphy/retry
    :ui.action/handler (fn [{:keys [store]}]
                         ;; The failed search, even if the input changed since:
                         (search-with! store (fn [panel]
                                               (assoc panel :search-value (:query panel "")))))}
   {:ui.action/kind :editor2.giphy/unmount
    :ui.action/handler (fn [{:keys [store action]}]
                         (swap! store unmount (second action)))}
   {:ui.action/kind :editor2.giphy/select
    :ui.action/handler select!}])

(comment
  (api-url default-panel-state)
  ;; => "https://api.giphy.com/v1/stickers/trending?api_key=...&limit=20&offset=0"

  (api-url (start-search default-panel-state
                         assoc :mode :gifs :search-value "cat & dog"))

  (start-load-more {:search-offset 20 :gifs [{:id "a"}]})
  ;; => {:search-offset 40, :gifs [{:id "a"}], :loading-more? true}
  )
